import React from "react";

// Web Vitals monitoring utility for tracking Core Web Vitals and performance metrics

export interface WebVitals {
  FCP: number | null; // First Contentful Paint
  LCP: number | null; // Largest Contentful Paint
  FID: number | null; // First Input Delay
  CLS: number | null; // Cumulative Layout Shift
  TTFB: number | null; // Time to First Byte
  TTI: number | null; // Time to Interactive
}

export interface PerformanceMetric {
  name: string;
  value: number;
  rating: "good" | "needs-improvement" | "poor";
  timestamp: number;
}

// Web Vitals thresholds
const THRESHOLD_VALUES = {
  FCP: { good: 1800, poor: 3000 },
  LCP: { good: 2500, poor: 4000 },
  FID: { good: 100, poor: 300 },
  CLS: { good: 0.1, poor: 0.25 },
  TTFB: { good: 800, poor: 1800 },
  TTI: { good: 3800, poor: 7300 },
};

// Utility functions for web vitals
const getRating = (name: string, value: number): "good" | "needs-improvement" | "poor" => {
  const threshold = THRESHOLD_VALUES[name as keyof typeof THRESHOLD_VALUES];
  if (!threshold) return "good";

  if (value <= threshold.good) return "good";
  if (value <= threshold.poor) return "needs-improvement";
  return "poor";
};

class WebVitalsMonitor {
  private metrics: WebVitals = {
    FCP: null,
    LCP: null,
    FID: null,
    CLS: null,
    TTFB: null,
    TTI: null,
  };

  private observers: PerformanceObserver[] = [];
  private isInitialized = false;

  constructor() {
    this.init();
  }

  public init() {
    if (this.isInitialized || typeof window === "undefined") return;

    this.isInitialized = true;
    this.observeFCP();
    this.observeLCP();
    this.observeFID();
    this.observeCLS();
    this.measureTTFB();
    this.measureTTI();
  }

  private observeFCP() {
    if ("PerformanceObserver" in window) {
      const observer = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          if (entry.name === "first-contentful-paint") {
            this.metrics.FCP = entry.startTime;
            this.trackMetric("FCP", this.metrics.FCP);
          }
        }
      });
      observer.observe({ entryTypes: ["paint"] });
    }
  }

  private observeLCP() {
    if ("PerformanceObserver" in window) {
      const observer = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          if (entry.name === "largest-contentful-paint") {
            this.metrics.LCP = entry.startTime;
            this.trackMetric("LCP", this.metrics.LCP);
          }
        }
      });
      observer.observe({ entryTypes: ["largest-contentful-paint"] });
    }
  }

  private observeFID() {
    if ("PerformanceObserver" in window) {
      const observer = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          if (entry.name === "first-input") {
            const fidEntry = entry as PerformanceEventTiming;
            this.metrics.FID = fidEntry.processingStart - fidEntry.startTime;
            this.trackMetric("FID", this.metrics.FID);
          }
        }
      });
      observer.observe({ entryTypes: ["first-input"] });
    }
  }

  private observeCLS() {
    if ("PerformanceObserver" in window) {
      let clsValue = 0;
      const observer = new PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          const clsEntry = entry as any;
          if (clsEntry.hadRecentInput) continue;
          clsValue += clsEntry.value;
          this.metrics.CLS = clsValue;
          this.trackMetric("CLS", this.metrics.CLS);
        }
      });
      observer.observe({ entryTypes: ["layout-shift"] });
    }
  }

  private measureTTFB() {
    if ("performance" in window) {
      const navigation = performance.getEntriesByType(
        "navigation"
      )[0] as PerformanceNavigationTiming;
      if (navigation) {
        this.metrics.TTFB = navigation.responseStart - navigation.requestStart;
        this.trackMetric("TTFB", this.metrics.TTFB);
      }
    }
  }

  private measureTTI() {
    // TTI is more complex to measure, using a simplified approach
    if ("performance" in window) {
      const navigation = performance.getEntriesByType(
        "navigation"
      )[0] as PerformanceNavigationTiming;
      if (navigation) {
        this.metrics.TTI = navigation.loadEventEnd - navigation.fetchStart;
        this.trackMetric("TTI", this.metrics.TTI);
      }
    }
  }

  private trackMetric(name: string, value: number) {
    // Send to analytics service
    if (typeof window !== "undefined" && window.gtag) {
      window.gtag("event", "web_vital", {
        event_category: "Web Vitals",
        event_label: name,
        value: Math.round(value),
      });
    }
  }

  // Get current metrics
  getMetrics(): WebVitals {
    return { ...this.metrics };
  }

  // Get metric with rating
  getMetricWithRating(name: keyof WebVitals): PerformanceMetric | null {
    const value = this.metrics[name];
    if (value === null) return null;

    return {
      name,
      value,
      rating: getRating(name, value),
      timestamp: Date.now(),
    };
  }

  // Get overall performance score (0-100)
  getPerformanceScore(): number {
    const metrics = Object.values(this.metrics).filter((v) => v !== null);
    if (metrics.length === 0) return 0;

    let totalScore = 0;
    metrics.forEach((value, index) => {
      const metricName = Object.keys(this.metrics)[index] as keyof WebVitals;
      const rating = getRating(metricName, value!);

      switch (rating) {
        case "good":
          totalScore += 100;
          break;
        case "needs-improvement":
          totalScore += 50;
          break;
        case "poor":
          totalScore += 0;
          break;
      }
    });

    return Math.round(totalScore / metrics.length);
  }

  // Cleanup observers
  destroy() {
    this.observers.forEach((observer) => observer.disconnect());
    this.observers = [];
  }
}

// Export singleton instance
export const webVitalsMonitor = new WebVitalsMonitor();

// Convenience functions
export const getWebVitals = () => webVitalsMonitor.getMetrics();
export const getPerformanceScore = () => webVitalsMonitor.getPerformanceScore();
export const getMetricWithRating = (name: keyof WebVitals) =>
  webVitalsMonitor.getMetricWithRating(name);

// React hook for components to access web vitals
export const useWebVitals = () => {
  const [metrics, setMetrics] = React.useState<WebVitals>(
    webVitalsMonitor.getMetrics()
  );
  const [score, setScore] = React.useState<number>(
    webVitalsMonitor.getPerformanceScore()
  );

  React.useEffect(() => {
    const handleWebVital = (event: CustomEvent<PerformanceMetric>) => {
      const { name, value } = event.detail;
      setMetrics((prev) => ({ ...prev, [name]: value }));
      setScore(webVitalsMonitor.getPerformanceScore());
    };

    window.addEventListener("web-vital", handleWebVital as EventListener);
    return () =>
      window.removeEventListener("web-vital", handleWebVital as EventListener);
  }, []);

  return {
    metrics,
    score,
    getMetricWithRating:
      webVitalsMonitor.getMetricWithRating.bind(webVitalsMonitor),
  };
};
